/**
 * Competition settings, entry fees and guest rules — the resolvers that read
 * them. (What a new competition's `settings` start from is the shared
 * `buildCompetitionDraft`'s, in `packages/shared/src/competitionDraft.js` — the
 * MCP server creates drafts with it too.) The resolvers exist because almost
 * every setting has a fallback chain (a group's fee falls back to the
 * competition's, a proxy-guest limit to a default and then to bounds) and
 * reading that chain by hand at each call site is how two surfaces end up
 * disagreeing about the same competition.
 */

/**
 * How an entry fee was handed over. Recorded per registration so the organiser
 * can reconcile the till against the entry list — cash is the one that has to
 * add up at the end of the day, the other two are checked against a statement.
 *
 * Stored on `registrations/{uid}.paymentMethod`; `null` means not recorded.
 * On the registration rule's self-update denylist — only staff may write it.
 */
export const PAYMENT_METHODS = ["card", "transfer", "cash"];

/**
 * Resolve the entry fee for a specific group.
 * Falls back to the competition default if the group has no override.
 * Returns { amount, currency } or null if entry fee is disabled.
 */
export function getGroupEntryFee(competition, group) {
  if (!competition?.settings?.entryFee?.enabled) return null;
  const amount = group?.entryFeeAmount ?? competition.settings.entryFee.amount;
  return { amount, currency: competition.settings.entryFee.currency };
}

// ── Expected entry fee — the ONE formula ───────────────────────────────────
//
// `computePayerFee` / `entryFeeSettled` / `feeByGroupFromDocs` live in the
// shared package: the registration callable and the post-guest-removal
// recompute run the very same copy the card and the fee panel do. (Until the
// 2026-09 audit follow-up (D1) the client keyed the own fee on HAVING A GROUP
// while the server — which decides `entryFeePaid` — keyed it on competing;
// the server's rule won, and phase 1 of the shared-package plan merged the
// two copies.)
import { computePayerFee, feeByGroupFromDocs, earlyBirdPct, GUEST_MODES, resolveGuestMode, competitionSource } from "@sendly/shared";

/**
 * Total expected entry fee for a payer and their proxy guests, from the
 * competition document and its loaded groups — `computePayerFee` with the
 * inputs resolved. Each entry is priced at the early-bird discount its OWN
 * registration snapshot (`earlyBird`) carries, so the caller hands over the
 * registrations rather than bare group ids.
 * @param {object} competition - competition doc
 * @param {object} params
 * @param {boolean} params.competes - the payer holds a competitor entry of their own
 * @param {string|null} params.ownGroupId - the payer's own groupId, if any
 * @param {object|null} [params.ownEarlyBird] - the payer's own registration `earlyBird` snapshot
 * @param {Array<{ groupId: string|null, earlyBird?: object }>} [params.guests] - one
 *   per guest: its registration (groupId + its own `earlyBird` snapshot)
 * @param {Array} [params.guestGroupIds] - legacy shape, one groupId per guest, no discount;
 *   read only when `guests` is absent
 * @param {Array} params.groups - loaded groups array (may be empty → defaults apply)
 * @returns {{ ownAmount: number, guestAmounts: number[], totalAmount: number,
 *   fullAmount: number, discountAmount: number, currency: string } | null}
 */
export function calcExpectedFee(competition, {
  competes = false, ownGroupId = null, ownEarlyBird = null, guests, guestGroupIds = [], groups = [],
} = {}) {
  if (!competition?.settings?.entryFee?.enabled) return null;
  const currency = competition.settings.entryFee.currency || "HUF";
  const fee = computePayerFee({
    defaultAmount: competition.settings.entryFee.amount,
    feeByGroup: feeByGroupFromDocs(groups),
    competes,
    ownGroupId,
    ownDiscountPct: earlyBirdPct(ownEarlyBird),
    guests: Array.isArray(guests)
      ? guests.map((g) => ({ groupId: g?.groupId ?? null, discountPct: earlyBirdPct(g?.earlyBird) }))
      : (guestGroupIds || []).map((groupId) => ({ groupId, discountPct: 0 })),
  });
  return { ...fee, currency };
}

// ── Guests ──────────────────────────────────────────────────────────────────
//
// The guest mode, the proxy-guest limit and their constants are the shared
// package's (`GUEST_MODES`, `resolveGuestMode`, `resolveProxyGuestLimit`, …):
// the registration callable refuses on the very same rule the "+ Vendég"
// button and the "1 / 2" hint are rendered from.


/** Guests may exist on this competition — gates the organizer-side surfaces. */
export function guestsAllowed(comp) {
  return resolveGuestMode(comp) !== GUEST_MODES.OFF;
}

/** A regular competitor may register a guest — gates the "+ Vendég" button. */
export function proxyGuestsAllowed(comp) {
  return resolveGuestMode(comp) === GUEST_MODES.ANYONE;
}

// ── Which competition is "the" active one ────────────────────────────────────
//
// Three types share the `competitions` collection (see competition/actions.js):
// `standard` (groups, sessions, scorecards), `challenge` (a passive board) and
// `external` (a link to a competition held elsewhere). Only a standard one has
// the subcollections the map integration listens on, and more than one comp
// may be `active` at once (a long external one beside this weekend's real one).
//
// Five hooks used to answer "which is the active competition?" with their own
// inline `find`, and two of the five forgot the external exclusion. Firestore
// hands the list back in document-id order, so the OLDER external comp won:
// `activeCompSessions` resolved to `[]`, `compMapContext` fell through, and a
// competitor's tap on a competition route opened the ordinary route modal —
// and logged a gym ascent that nothing can tell apart afterwards. One helper,
// and `activeCompetition.test.js` scans src/hooks so no sixth copy appears.

/** A competition with groups / sessions / registrations — not a challenge, not external. */
export function isStandardCompetition(comp) {
  if (!comp) return false;
  const type = comp.type || "standard";
  return type !== "challenge" && type !== "external";
}

/** Every active standard competition, in the order the caller holds them. */
export function activeStandardCompetitions(competitions) {
  if (!Array.isArray(competitions)) return [];
  return competitions.filter(c => c && c.status === "active" && isStandardCompetition(c));
}

/**
 * The first active standard competition WITH THE ROUTE SOURCE, or null — the
 * one the map integration belongs to. A spray competition (`settings.source:
 * "spray"`) has no map routes; picking it would hand the map's route hiding,
 * 🏆 filter and tap-to-score to a competition whose ids are spray problems,
 * and a route competition running beside it would lose them.
 */
export function findActiveStandardCompetition(competitions) {
  return activeStandardCompetitions(competitions).find(c => competitionSource(c) === "routes") || null;
}

/**
 * Format a currency amount for display.
 * HUF: space-separated thousands, no decimals (e.g. "3 000").
 * EUR/USD: two decimals (e.g. "25.00").
 */
export function formatCurrency(amount, currency) {
  if (amount == null) return "";
  const num = Number(amount);
  if (isNaN(num)) return "";
  if (currency === "HUF") {
    return Math.round(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  }
  return num.toFixed(2);
}
